import React, { useEffect, useRef, useState } from 'react';
import {
    Bold, Italic, Link2, Heading, List, AlignLeft, AlignCenter, Square, Code, Eye, Upload,
} from 'lucide-react';

const toolBtn = "px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-600 cursor-pointer transition-all hover:bg-gray-50 hover:text-gray-900 flex items-center gap-1.5 text-[13px]";

const escapeHtml = value => value.replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/*
    Оформление внутри визуального режима.

    Tailwind сбрасывает заголовки и маркеры списков на уровне страницы, а редактор пишет
    обычные <h2> и <ul> - те самые, которые почтовый клиент покажет со своим оформлением.
    Без этих правил нажатая кнопка «заголовок» не меняла бы на вид ничего, и человек
    нажимал бы её второй раз, ломая разметку письма. Правила ограничены полем редактора,
    чтобы не разъехалась остальная админка.
*/
const EDITOR_STYLE = `
.mail-editor h1 { font-size: 26px; font-weight: 700; margin: 0 0 14px; }
.mail-editor h2 { font-size: 21px; font-weight: 700; margin: 0 0 12px; }
.mail-editor h3 { font-size: 17px; font-weight: 600; margin: 0 0 10px; }
.mail-editor p { margin: 0 0 12px; }
.mail-editor ul { list-style: disc; padding-left: 22px; margin: 0 0 12px; }
.mail-editor ol { list-style: decimal; padding-left: 22px; margin: 0 0 12px; }
.mail-editor li { margin: 0 0 4px; }
.mail-editor a { color: #2563eb; text-decoration: underline; }
.mail-editor blockquote { border-left: 3px solid #e5e7eb; padding-left: 12px; color: #6b7280; margin: 0 0 12px; }
.mail-editor img { max-width: 100%; }
`;

/**
 * Редактор письма: визуальный режим и режим разметки.
 *
 * <p>Визуальный сделан на contentEditable, а не на готовой библиотеке. Редакторы общего
 * назначения выдают разметку для страницы - flex, классы, внешние стили, - а в письме это
 * не работает: почтовые клиенты понимают таблицы и стили в атрибуте. Здесь набор действий
 * маленький и каждое даёт разметку, которая доживает до почтового клиента.
 *
 * <p>Режим разметки нужен рядом с визуальным по той же причине: сложную вёрстку письма
 * всё равно готовят в стороннем шаблоне и вставляют кодом, а править потом текст удобнее
 * глазами.
 */
const HtmlEditor = ({ value, onChange, onPreview, previewHtml, previewLoading, variables, allowVisual = true, allowPreview = true }) => {
    const [mode, setMode] = useState(allowVisual ? 'visual' : 'html'); // 'visual' | 'html' | 'preview'
    const editorRef = useRef(null);
    const sourceRef = useRef(null);
    const fileRef = useRef(null);
    const [lastRange, setLastRange] = useState(null);

    // head и стили полного документа нельзя вставлять в contentEditable: они меняют
    // саму админку, а браузер при сохранении теряет структуру исходного письма.
    const visualAvailable = allowVisual && !/<!doctype\b|<\s*(html|head|body|style|link|meta|base|script|iframe|object|embed|form|svg|math)\b|\bon\w+\s*=|javascript\s*:/i.test(value || '');
    const activeMode = mode === 'visual' && !visualAvailable ? 'html' : mode;

    // Разметку в contentEditable пишем только когда она пришла снаружи: иначе каждый
    // набранный символ перерисовывал бы поле и уносил курсор в начало.
    useEffect(() => {
        if (activeMode === 'visual' && editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value || '';
        }
    }, [activeMode, value]);

    const rememberSelection = () => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
            setLastRange(selection.getRangeAt(0).cloneRange());
        }
    };

    const exec = (command, argument) => {
        editorRef.current?.focus();
        // execCommand объявлен устаревшим, но замены для contentEditable так и не появилось,
        // и во всех браузерах он работает.
        document.execCommand(command, false, argument);
        onChange(editorRef.current?.innerHTML || '');
    };

    const insertHtml = (html) => {
        if (activeMode === 'html') {
            const start = sourceRef.current?.selectionStart ?? (value || '').length;
            const end = sourceRef.current?.selectionEnd ?? start;
            onChange((value || '').slice(0, start) + html + (value || '').slice(end));
            return;
        }
        editorRef.current?.focus();
        // Возвращаем курсор туда, где он был до нажатия на кнопку панели.
        if (lastRange) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(lastRange);
        }
        document.execCommand('insertHTML', false, html);
        onChange(editorRef.current?.innerHTML || '');
    };

    const insertLink = () => {
        const url = window.prompt('Адрес ссылки', 'https://');
        if (!url) return;
        if (!/^https?:\/\//i.test(url)) {
            window.alert('Ссылка должна начинаться с http:// или https://');
            return;
        }
        exec('createLink', url);
    };

    /** Кнопка письма - таблицей: у ссылки с отступами Outlook игнорирует padding. */
    const insertButton = () => {
        const text = window.prompt('Текст кнопки', 'Перейти');
        if (!text) return;
        const url = window.prompt('Адрес кнопки', 'https://');
        if (!url || !/^https?:\/\//i.test(url)) return;

        insertHtml(
            `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:20px 0;">`
            + `<tr><td style="border-radius:10px; background-color:#4c00ff;">`
            + `<a href="${escapeHtml(url)}" style="display:inline-block; padding:13px 26px; font-family:Inter,-apple-system,'Segoe UI',Roboto,Arial,sans-serif;`
            + ` font-size:15px; font-weight:600; color:#ffffff; text-decoration:none; border-radius:10px;">${escapeHtml(text)}</a>`
            + `</td></tr></table><p><br></p>`);
    };

    const handleUpload = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        if (file.size > 512 * 1024) {
            window.alert('Файл больше 512 КБ');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            onChange(String(reader.result || ''));
            // Загруженный шаблон показываем кодом: чужая вёрстка обычно сложнее того, что
            // визуальный режим умеет править, и открывать её там - значит её испортить.
            setMode('html');
        };
        reader.onerror = () => window.alert('Не удалось прочитать HTML-файл');
        reader.readAsText(file);
    };

    const modeButton = (id, label, icon) => (
        <button
            type="button"
            disabled={id === 'visual' && !visualAvailable}
            aria-pressed={activeMode === id}
            onClick={() => { if (id === 'preview') onPreview(); setMode(id); }}
            className={`px-3 py-1.5 rounded-md text-[13px] font-medium cursor-pointer transition-all border flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${
                activeMode === id ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
        >
            {icon} {label}
        </button>
    );

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <style>{EDITOR_STYLE}</style>
            <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-50 border-b border-gray-200">
                {allowVisual && modeButton('visual', 'Визуально', <Eye size={14} />)}
                {modeButton('html', 'Разметка', <Code size={14} />)}
                {allowPreview && modeButton('preview', 'Как в почте', <Eye size={14} />)}

                <span className="w-px h-6 bg-gray-200 mx-1" />

                <button type="button" className={toolBtn} onClick={() => fileRef.current?.click()} title="Загрузить .html">
                    <Upload size={14} /> Файл
                </button>
                <input ref={fileRef} type="file" accept=".html,.htm,text/html" className="hidden" onChange={handleUpload} />
            </div>

            {allowVisual && !visualAvailable && (
                <p className="m-0 px-4 py-3 text-[13px] text-gray-500 bg-gray-50">
                    У этого письма своя вёрстка. Редактируйте HTML, чтобы сохранить его стили и структуру.
                </p>
            )}

            {activeMode === 'visual' && (
                <>
                    <div className="flex flex-wrap items-center gap-1.5 p-2 border-b border-gray-200 bg-white">
                        <button type="button" className={toolBtn} onClick={() => exec('bold')} title="Жирный"><Bold size={14} /></button>
                        <button type="button" className={toolBtn} onClick={() => exec('italic')} title="Курсив"><Italic size={14} /></button>
                        <button type="button" className={toolBtn} onClick={() => exec('formatBlock', 'h2')} title="Заголовок"><Heading size={14} /></button>
                        <button type="button" className={toolBtn} onClick={() => exec('insertUnorderedList')} title="Список"><List size={14} /></button>
                        <button type="button" className={toolBtn} onClick={() => exec('justifyLeft')} title="По левому краю"><AlignLeft size={14} /></button>
                        <button type="button" className={toolBtn} onClick={() => exec('justifyCenter')} title="По центру"><AlignCenter size={14} /></button>
                        <button type="button" className={toolBtn} onClick={insertLink} title="Ссылка"><Link2 size={14} /></button>
                        <button type="button" className={toolBtn} onClick={insertButton} title="Кнопка"><Square size={14} /> Кнопка</button>
                    </div>

                    <div
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        onInput={e => onChange(e.currentTarget.innerHTML)}
                        onKeyUp={rememberSelection}
                        onMouseUp={rememberSelection}
                        onBlur={rememberSelection}
                        className="mail-editor min-h-[320px] max-h-[520px] overflow-y-auto p-4 text-[15px] leading-relaxed focus:outline-none bg-white"
                    />
                </>
            )}

            {activeMode === 'html' && (
                <textarea
                    ref={sourceRef}
                    aria-label="HTML письма"
                    className="w-full min-h-[320px] max-h-[520px] p-4 font-mono text-[13px] leading-relaxed focus:outline-none resize-y"
                    value={value || ''}
                    onChange={e => onChange(e.target.value)}
                    spellCheck={false}
                />
            )}

            {activeMode === 'preview' && (
                previewLoading ? (
                    <div className="h-[420px] flex items-center justify-center text-gray-500 bg-gray-50">
                        Собираем письмо…
                    </div>
                ) : previewHtml ? (
                    <iframe
                        title="Предпросмотр письма"
                        srcDoc={previewHtml || ''}
                        sandbox=""
                        className="w-full h-[420px] bg-white"
                    />
                ) : (
                    <div className="h-[420px] flex items-center justify-center text-gray-500 bg-gray-50 p-4 text-center">
                        Письмо изменилось или превью ещё не собрано. Нажмите «Как в почте» для обновления.
                    </div>
                )
            )}

            {activeMode !== 'preview' && variables?.length > 0 && (
                <div className="p-3 bg-gray-50 border-t border-gray-200">
                    <div className="text-[13px] text-gray-500 mb-2">
                        Данные пользователя — нажмите, чтобы вставить:
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {variables.map(variable => (
                            <button
                                key={variable.name}
                                type="button"
                                title={`${variable.label} — например, ${variable.sample}`}
                                onClick={() => insertHtml(`{{${variable.name}}}`)}
                                className="px-2.5 py-1 rounded-md border border-gray-300 bg-white text-[12px] font-mono text-gray-700 cursor-pointer hover:border-blue-400 hover:text-blue-700"
                            >
                                {'{{'}{variable.name}{'}}'}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HtmlEditor;
